import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VenueContext = createContext(null);

const sortVenueApps = (venueApps = []) => {
  return [...venueApps].sort((a, b) => {
    const aHasDisplayName = Boolean((a.map_name || a.restaurant_name || '').trim());
    const bHasDisplayName = Boolean((b.map_name || b.restaurant_name || '').trim());
    if (aHasDisplayName !== bHasDisplayName) {
      return aHasDisplayName ? -1 : 1;
    }

    const aIsLive = a.status === 'live';
    const bIsLive = b.status === 'live';
    if (aIsLive !== bIsLive) {
      return aIsLive ? -1 : 1;
    }

    return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
  });
};

export function VenueProvider({ children, scope = 'owner' }) {
  const [activeVenueAppId, setActiveVenueAppId] = useState(() => localStorage.getItem('activeVenueAppId') || null);
  const [activeVenue, setActiveVenue] = useState(null);
  const [ownerVenues, setOwnerVenues] = useState([]);
  const [allVenues, setAllVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOwnerVenues = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/venue/applications/my-all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // `/venue/applications/my-all` returns BOTH venues this user owns AND
      // (appended, tagged with `staff_role`) the single venue they're a
      // staff/admin member of elsewhere. Those two must never be mixed in
      // the same list: showing a staff-only venue inside the owner app's
      // switcher (no owner rights on it) let a venue owner accidentally
      // "select" someone else's venue with zero badge/warning, which then
      // 404s on every owner-only settings call and looks like their own
      // venue vanished. `scope` keeps each app strictly on its own venues
      // for the DEFAULT active-venue resolution below. `allVenues` (every
      // venue the user has any relationship to, owned + staff, each item
      // tagged with a role) is exposed separately for the venue-switcher
      // dropdown, which explicitly lets the user cross between their own
      // venue(s) and their staff venue via a deliberate tap (handled by
      // `useVenueSwitcher`), not by silent default resolution.
      // Owner switcher must show every non-abandoned venue the owner has —
      // including `submitted` and `pending` states. Previously this list
      // whitelisted only `live | approved | setup_mode`, which meant that
      // the moment the 10th photo landed and the backend flipped the new
      // venue from `setup_mode` → `submitted` (see
      // routes/venue_applications.py::create_venue_application), the
      // switcher silently dropped it and fell back to whichever other
      // venue was scoped[0]. From the owner's POV the venue "disappeared".
      // Blacklist obviously-hidden states instead so any future backend
      // status (e.g. `revisions_requested_media_only`) still shows up.
      const HIDDEN_STATUSES = new Set(['draft', 'rejected', 'deleted', 'archived']);
      const approved = sortVenueApps(
        res.data.filter((a) => !HIDDEN_STATUSES.has((a.status || '').toLowerCase())),
      );
      setAllVenues(approved);
      const scoped = scope === 'staff'
        ? approved.filter((v) => v.staff_role)
        : approved.filter((v) => !v.staff_role);
      setOwnerVenues(scoped);

      if (scope === 'staff') {
        // A staff member only ever has one venue (`venue_member_app_id`).
        // Always resolve directly to it — never trust a cached
        // `activeVenueAppId` here, since that value is shared with the
        // owner app and may point at a venue owned elsewhere.
        const staffVenue = scoped[0] || null;
        if (staffVenue) {
          setActiveVenueAppId(staffVenue.id);
          setActiveVenue(staffVenue);
          localStorage.setItem('activeVenueAppId', staffVenue.id);
        } else {
          setActiveVenue(null);
        }
      } else if (!activeVenueAppId && scoped.length > 0) {
        // If no active venue set, pick the first approved one
        setActiveVenueAppId(scoped[0].id);
        setActiveVenue(scoped[0]);
        localStorage.setItem('activeVenueAppId', scoped[0].id);
      } else if (activeVenueAppId) {
        const found = scoped.find(v => v.id === activeVenueAppId);
        if (found) {
          setActiveVenue(found);
        } else if (scoped.length > 0) {
          // Active venue was removed/rejected, or the cached id belonged to
          // a staff-only venue not owned by this user — fall back to the
          // first venue actually owned.
          setActiveVenueAppId(scoped[0].id);
          setActiveVenue(scoped[0]);
          localStorage.setItem('activeVenueAppId', scoped[0].id);
        } else {
          setActiveVenue(null);
        }
      }
    } catch (err) {
      console.error('Failed to load venues', err);
    } finally {
      setLoading(false);
    }
  }, [activeVenueAppId, scope]);

  useEffect(() => {
    fetchOwnerVenues();
  }, []);

  // When mounting any /venue/* route, ensure the user's active role is
  // `venue_owner` if their account has it available. Without this, a
  // multi-role user who logged in as `guest` would hit 403s on venue-only
  // endpoints (e.g. POST /api/transactions) the moment they try to scan.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const stored = localStorage.getItem('user');
        const cachedUser = stored ? JSON.parse(stored) : null;
        const role = cachedUser?.role;
        const available = cachedUser?.available_roles || [];
        if (role === 'venue_owner' || role === 'admin' || role === 'staff') return;
        if (!available.includes('venue_owner')) return;

        const res = await axios.post(
          `${API}/auth/switch-role`,
          { target_role: 'venue_owner' },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        const { access_token, user: nextUser } = res.data || {};
        if (access_token) localStorage.setItem('token', access_token);
        if (nextUser) localStorage.setItem('user', JSON.stringify(nextUser));
      } catch (err) {
        // Non-fatal — backend now also authorizes by available roles, so the
        // scan flow still works even if this auto-switch fails.
        console.warn('Auto-switch to venue_owner failed', err?.response?.data || err.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const switchVenue = useCallback((venueAppId) => {
    const venue = ownerVenues.find(v => v.id === venueAppId);
    if (venue) {
      setActiveVenueAppId(venueAppId);
      setActiveVenue(venue);
      localStorage.setItem('activeVenueAppId', venueAppId);
    }
  }, [ownerVenues]);

  const refresh = useCallback(() => {
    fetchOwnerVenues();
  }, [fetchOwnerVenues]);

  return (
    <VenueContext.Provider value={{
      activeVenueAppId,
      activeVenue,
      ownerVenues,
      allVenues,
      scope,
      switchVenue,
      refresh,
      loading,
    }}>
      {children}
    </VenueContext.Provider>
  );
}

export function useVenue() {
  const ctx = useContext(VenueContext);
  if (!ctx) throw new Error('useVenue must be used within VenueProvider');
  return ctx;
}

export default VenueContext;
